import { useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { confirm } from 'actions/app';
import { hideReferenceReleaseComposer, showReferenceReleaseComposer } from 'actions/joborder';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useShowAlert } from 'hooks';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { useUserContext } from 'providers/UserProvider';
import { EntityRoutes } from 'routes/constants';
import API from 'services/API';
import {
  canUserEditEntity,
  canUserOrStaffEditEntity,
  Permissions,
  userHasPermission,
  userHasRoles
} from 'services/Authorization';
import strings from 'strings';
import { Endpoints } from 'UI/constants/endpoints';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { navigateToSendout } from 'UI/pages/Sendouts/utils';
import { completeReferenceRelease } from 'UI/utils/inventory';

const COPIES = strings.inventoryProfiles.sections.candidates.unlinkHiringDialog;

/**
 * What the current user may see or do on a Candidate profile.
 * @param {Object} options
 * @param {Object} options.candidate Profile with `referenceReleases` attached by useCandidateProfile
 * @param {boolean} [options.isAlreadyAccountable] The user is already the recruiter: nothing to claim
 * @returns {Object} Named booleans
 */
export const useCandidatePermissions = ({ candidate, isAlreadyAccountable = false }) => {
  const [user] = useUserContext();
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const canSeePlacements = userHasPermission(Permissions.Placements.CanUseModule);

  return {
    canClaimAccountability:
      checkIfFeatureFlagEnabled(FeatureFlags.ClaimAccountabilityCandidate) && !isAlreadyAccountable,
    canEdit: canUserOrStaffEditEntity(user, candidate),
    canOpenReferenceRelease:
      (canUserEditEntity(user, candidate, { includeCollaborator: true }) ||
        candidate.referenceReleases?.length > 0) &&
      canSeePlacements,
    canRequestDoNotContact: checkIfFeatureFlagEnabled(FeatureFlags.RequestDoNotContactFromProfile),
    canRequestDoNotEngage: checkIfFeatureFlagEnabled(FeatureFlags.RequestDoNotEngageFromProfile),
    canSeePlacements,
    canUseDailyPlan:
      checkIfFeatureFlagEnabled(FeatureFlags.DailyPlanAddTaskFromProfile) &&
      userHasPermission(Permissions.DailyPlan.CanUseModule),
    canUsePresentationMaker: userHasPermission(Permissions.Inventory.PresentationMaker),
    canUseRoleplay:
      checkIfFeatureFlagEnabled(FeatureFlags.AIRoleplay) &&
      userHasPermission(Permissions.AIIntegrations.Roleplay),
    canVerifyUrgency:
      checkIfFeatureFlagEnabled(FeatureFlags.UrgencyVerification) &&
      userHasRoles([Roles.RegionalDirector, Roles.AssistantRegionalDirector, Roles.Leadership]),
    hasHiringAuthority: Boolean(candidate.relation?.hiring_authority_id)
  };
};

/**
 * Commands of the Candidate profile: unlink the hiring authority, navigation and the reference
 * release composer, which stays in Redux because shared placement components open it.
 * @param {Object} params
 * @param {number|string} params.id Candidate id
 * @param {() => void} params.refresh Reloads the profile silently
 * @param {() => void} params.refreshReferenceReleases Reloads the reference releases
 * @param {string} [params.hiringName] Name of the linked hiring authority, for the unlink dialog
 * @param {number} [params.hiringAuthorityId] Id of the linked hiring authority, for the unlink call
 */
export const useCandidateActions = ({
  hiringAuthorityId,
  hiringName,
  id,
  refresh,
  refreshReferenceReleases
}) => {
  const dispatch = useDispatch();
  const { showAlert } = useShowAlert();
  const history = useHistory();
  const { referenceRelease } = useSelector(state => state.jobOrder);
  const isUnlinkingRef = useRef(false);

  const unlink = async ok => {
    if (!ok || isUnlinkingRef.current) return;
    isUnlinkingRef.current = true;

    try {
      const response = await API.delete(
        `${Endpoints.Candidates}/${id}/${Endpoints.HiringAuthorities}/${hiringAuthorityId}/${Endpoints.Unlink}`
      );
      if (response?.status !== HTTPStatusCodes.NoContent) return;

      showAlert({
        title: COPIES.hiringAuthorityUnlinked,
        body: strings.formatString(COPIES.unlinkedBody, { hiringName })
      });
      refresh();
    } catch (error) {
      showAlert({ title: COPIES.hiringAuthorityError, error });
    } finally {
      isUnlinkingRef.current = false;
    }
  };

  const unlinkHiringAuthority = () =>
    dispatch(
      confirm({
        severity: 'warning',
        title: COPIES.title,
        message: strings.formatString(COPIES.message, { hiringName }),
        confirmButtonText: COPIES.confirmButtonText,
        cancelButtonText: COPIES.cancelButtonText,
        mustComplete: true,
        onConfirm: unlink
      })
    );

  const refreshReleasesAndProfile = () => {
    refreshReferenceReleases();
    refresh();
  };

  const openReferenceRelease = () =>
    dispatch(
      showReferenceReleaseComposer({
        candidateId: id,
        onReferenceReleaseComplete: refreshReleasesAndProfile
      })
    );
  const closeReferenceRelease = () => dispatch(hideReferenceReleaseComposer());
  const { handleReferenceReleaseComplete } = completeReferenceRelease({
    referenceRelease,
    referenceReleaseHandler: closeReferenceRelease
  });

  const goToHAProfile = hiringAuthorityProfileId =>
    history.push(EntityRoutes.HiringAuthorityProfile.replace(':id', hiringAuthorityProfileId));

  const onAddHiringAuthorityCompleted = data => {
    if (data?.id) goToHAProfile(data.id);
  };

  const onLinkHiringAuthorityCompleted = data => {
    if (data?.relation?.hiring_authority_id) goToHAProfile(data.relation.hiring_authority_id);
  };

  return {
    closeReferenceRelease,
    completeReferenceRelease: handleReferenceReleaseComplete,
    navigateFromSendout: navigateToSendout(history),
    onAddHiringAuthorityCompleted,
    onLinkHiringAuthorityCompleted,
    openDailyPlan: () => history.push(EntityRoutes.DailyPlan),
    openDataSheet: () => history.push(EntityRoutes.CandidateDataSheet.replace(':id', id)),
    openReferenceRelease,
    referenceReleaseCandidateId: referenceRelease?.candidateId,
    unlinkHiringAuthority
  };
};
